import React, { useEffect, useState, useRef } from "react";
import { Play, FastForward } from "lucide-react";
import { api } from "../lib/api";

/**
 * Sponsored preroll player. Renders the active preroll-video placement for
 * `location=lesson_player` before the real lesson video.
 *
 * Props:
 *   - lessonId: current lesson id (used to dedupe so the same preroll doesn't
 *     replay when navigating between lessons in the same session).
 *   - onFinish: callback to signal preroll is done — parent should swap in the
 *     real lesson video.
 */
const SEEN_PREROLLS_KEY = "fc_preroll_seen";

export default function LessonPreroll({ lessonId, onFinish }) {
  const [placement, setPlacement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const [canSkip, setCanSkip] = useState(false);
  const videoRef = useRef(null);
  const impressionFiredRef = useRef(false);

  // Find the first preroll placement (if any) — only once per session per
  // placement_id (we keep a list of seen IDs in sessionStorage).
  useEffect(() => {
    let alive = true;
    const seen = new Set(JSON.parse(sessionStorage.getItem(SEEN_PREROLLS_KEY) || "[]"));
    api.get("/sponsor-placements?location=lesson_player")
      .then((r) => {
        if (!alive) return;
        const prerolls = (r.data.placements || []).filter(
          (p) => p.type === "preroll_video" && !seen.has(p.id)
        );
        const next = prerolls[0] || null;
        setPlacement(next);
        if (!next) onFinish?.();
      })
      .catch(() => {
        if (alive) onFinish?.();
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [lessonId, onFinish]);

  // Fire impression + start countdown when placement loaded
  useEffect(() => {
    if (!placement || impressionFiredRef.current) return;
    impressionFiredRef.current = true;
    api.post("/sponsor-events/impression", { placement_id: placement.id }).catch(() => {});
    const skipAfter = placement.skip_after_seconds ?? 5;
    if (placement.mandatory && (skipAfter === null || skipAfter === undefined)) {
      setCanSkip(false);
    } else {
      setCountdown(skipAfter);
      const t = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) {
            setCanSkip(true);
            clearInterval(t);
            return 0;
          }
          return c - 1;
        });
      }, 1000);
      return () => clearInterval(t);
    }
  }, [placement]);

  const finish = () => {
    if (placement) {
      const seen = JSON.parse(sessionStorage.getItem(SEEN_PREROLLS_KEY) || "[]");
      if (!seen.includes(placement.id)) {
        seen.push(placement.id);
        sessionStorage.setItem(SEEN_PREROLLS_KEY, JSON.stringify(seen));
      }
    }
    onFinish?.();
  };

  const onClickCta = () => {
    if (!placement) return;
    api.post("/sponsor-events/click", { placement_id: placement.id }).catch(() => {});
    if (placement.cta_url) {
      window.open(placement.cta_url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading || !placement) return null;

  return (
    <div className="relative bg-black aspect-video max-w-5xl mx-auto" data-testid={`lesson-preroll-${placement.id}`}>
      <video
        ref={videoRef}
        src={placement.creative_url}
        autoPlay
        playsInline
        muted={false}
        onEnded={finish}
        className="w-full h-full bg-black"
        data-testid="preroll-video"
      />
      {/* Overlay */}
      <div className="pointer-events-none absolute top-3 left-3 flex flex-col gap-1 text-white">
        <span className="text-[10px] tracking-[0.22em] uppercase font-black bg-fc-red px-2 py-1 inline-block w-fit">
          SPONSORED
        </span>
        {placement.sponsor?.name && (
          <span className="text-[10px] tracking-wider uppercase text-gray-600 bg-black/40 px-2 py-0.5 inline-block w-fit">
            {placement.sponsor.name}
          </span>
        )}
      </div>
      <div className="absolute bottom-3 right-3 flex items-center gap-2">
        {placement.cta_text && placement.cta_url && (
          <button
            onClick={onClickCta}
            className="bg-white text-fc-navy text-[11px] tracking-[0.18em] uppercase font-black px-3 py-2 hover:bg-white/90"
            data-testid="preroll-cta"
          >
            {placement.cta_text} →
          </button>
        )}
        {canSkip ? (
          <button
            onClick={finish}
            className="bg-fc-red text-white text-[11px] tracking-[0.18em] uppercase font-black px-3 py-2 hover:bg-fc-red/85 flex items-center gap-1"
            data-testid="preroll-skip-btn"
          >
            <FastForward size={12} /> Skip Ad
          </button>
        ) : (
          <span className="bg-black/60 text-gray-600 text-[11px] tracking-[0.18em] uppercase px-3 py-2 flex items-center gap-1" data-testid="preroll-countdown">
            <Play size={12} /> Skip in {countdown}s
          </span>
        )}
      </div>
    </div>
  );
}
